
export type QuestionContent = {
  question: string;
  options?: string[];
  correctOption?: number;
  responseType?: 'audio' | 'video';
  language?: string;
  starterCode?: string;
};

export type CodeTestCase = {
  name: string;
  input: string;
  expected_output: string;
  hidden: boolean;
  weight: number;
};

export type CodeTests = {
  language: string;
  setup_code?: string;
  test_cases: CodeTestCase[];
  constraints: {
    time_limit_seconds: number;
    memory_limit_mb: number;
  };
};

export type QuestionType = "multiple_choice" | "essay" | "audio_response" | "video_response" | "coding";

export type MarkschemeType = {
  criteria?: {
    name: string;
    description: string;
    max_points: number;
  }[];
  total_points?: number;
  overall_guidance?: string;
  suggested_answer?: string;
  // Legacy fields for backward compatibility
  correctAnswer?: string;
  explanation?: string;
  keyPoints?: string[];
};

export type Question = {
  id: string;
  question_type: QuestionType;
  content: QuestionContent | Json;
  time_limit: number | null;
  order: number;
  marks: number;
  // New fields:
  skill: string | null;
  difficulty: 'easy' | 'medium' | 'hard' | null;
  markscheme: MarkschemeType | null;
  code_tests?: CodeTests | null;
};

export type Json =
  | string
  | number
  | boolean
  | null
  | { [key: string]: Json | undefined }
  | Json[];

export interface JobDetails {
  title?: string;
  description?: string;
  key_responsibilities?: string;
  years_of_experience?: string;
  location?: string;
}

export type WorkArrangementType = 'Full-Time' | 'Part-Time' | 'Freelance';

export interface Test {
  id: string;
  title: string;
  description: string;
  creator_id: string;
  key_responsibilities?: string;
  years_of_experience?: string;
  location: string;
  language: string;
  key_skills: string[];
  time_limit: number;
  status: string;
  publish_to_job_board: boolean;
  work_arrangement: WorkArrangementType;
  salary_range_min: number | null;
  salary_range_max: number | null;
  webcam_monitoring_enabled?: boolean;
  created_at: string;
  organization_id?: string | null;
}

// Helper function to check if content is QuestionContent
export function isQuestionContent(content: QuestionContent | Json): content is QuestionContent {
  const isValid = (
    typeof content === 'object' &&
    content !== null &&
    'question' in content &&
    typeof content.question === 'string'
  );
  return isValid;
}

// Type guard for multiple choice questions
export function isMultipleChoiceContent(content: QuestionContent): content is QuestionContent & { options: string[]; correctOption: number } {
  return (
    Array.isArray(content.options) &&
    content.options.length > 0 &&
    typeof content.correctOption === 'number'
  );
}
